import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function ShropshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Shropshire Pothole Claims | 159,000 Repairs in Five Years | Fixtyer"
        description="Shropshire Council filled 159,272 potholes in five years across a 5,166km rural network — yet 46% of roads haven't been condition-surveyed since 2020. See what the DfT's four AMBER scorecards mean for your claim."
        canonical="/councils/shropshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Shropshire', url: '/councils/shropshire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Shropshire is AMBER on all four DfT scorecards — can I still claim?',
            answer: 'Yes. An AMBER overall rating does not block claims under Section 41 of the Highways Act 1980. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate scorecard colours. Shropshire\'s own report admits unclassified roads have deteriorated through recent winters while 46% of its network has not been condition-surveyed since 2020.',
          },
          {
            question: 'What if my pothole was on an unclassified or C road?',
            answer: 'Unclassified roads make up 2,388km — 46% of Shropshire\'s network — and minor roads (most C roads and all unclassified roads) are surveyed only every four years. The council states the unclassified network has not been surveyed since 2020 and is not due until 2026. At the last available survey (2020–24), 16% of unclassified roads were in RED condition — roughly 382km of village routes, estate roads and rural lanes.',
          },
          {
            question: 'Does Shropshire\'s high preventative spend weaken my claim?',
            answer: 'Not necessarily. Shropshire projects 88.8% preventative spend in 2025/26 and earned an AMBER Spend scorecard — but preventative surface dressing does not eliminate potholes forming between inspections. The council still expects to fill around 35,000 potholes in 2025/26. Your claim depends on whether the specific defect was known about and repaired in time, not on the council\'s aggregate spend split.',
          },
          {
            question: 'Does the council\'s admission that unclassified roads have deteriorated help my claim?',
            answer: 'Yes. Shropshire states that "evidence from reactive repairs suggests that this area of the network has deteriorated through recent winters" on roads it has not condition-surveyed since 2020. That is documented knowledge of elevated deterioration risk on 46% of the network — which raises the bar for what a reasonable inspection regime looks like under Section 58.',
          },
          {
            question: 'Shropshire filled 34,293 potholes in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Filling potholes is reactive maintenance, not proof the underlying road is sound. Shropshire filled 34,293 potholes in 2024/25 — about 94 every day — while B and C roads still had 13% in RED condition at the last survey and the council projects another 34,000 pothole repairs in 2025/26. A network producing defects at that rate is one where potholes routinely form between inspections.',
          },
          {
            question: 'Why doesn\'t Shropshire publish year-by-year SCANNER data like other councils?',
            answer: 'Shropshire uses structured visual surveys rather than SCANNER laser surveys, stating these "provide a better representation of road condition compared to SCANNER surveys." It publishes condition data in multi-year periods (2020–22, 2022–24, 2020–24) rather than annual breakdowns. For your claim, the survey gaps and the council\'s own deterioration admissions matter more than the survey technology.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Shropshire Council"
        councilSlug="shropshire"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/shropshire"
        headline="Shropshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Shropshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Shropshire: England's Rural Roads, Four Years Unsurveyed
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Shropshire manages <strong className="text-white">5,166km of roads</strong> — one of England's largest 
            rural highway networks — and earns <strong className="text-amber-400">AMBER on all four DfT scorecards</strong>. 
            The council filled <strong className="text-rose-400">159,272 potholes in five years</strong>, yet admits 
            46% of its network has not been condition-surveyed since 2020 and has deteriorated through recent winters.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">2,388km</div>
            <div className="text-white font-medium mt-2">Unclassified roads — last surveyed 2020</div>
            <div className="text-brand-300 text-sm mt-1">
              46% of Shropshire's network surveyed every four years at most. The council admits this 
              area has deteriorated through recent winters — and won't be measured again until 2026.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Multi-year visual survey data from Shropshire's own transparency report — 
            stable A-roads, stubborn B/C decline, and a six-year blind spot on unclassified roads
          </p>

          {/* Network size */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">The network (5,166km total)</h3>
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">450km</div>
                <div className="text-sm text-surface-700">A roads (8.7%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">2,327km</div>
                <div className="text-sm text-surface-700">B and C roads (45.0%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">2,388km</div>
                <div className="text-sm text-amber-800">Unclassified (46.2%)</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              National Highways manages the M54, A5, A49 south of A5, A458 west of A5 and A483. 
              Shropshire Council maintains everything else — overwhelmingly rural B, C and unclassified roads.
            </p>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (450km): stable but not spotless</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">9%</div>
                <div className="text-sm text-rose-800">RED</div>
                <div className="text-xs text-surface-500">unchanged 2020–22 and 2022–24</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">7–8%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">7% (2020–22), 8% (2022–24)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">83–84%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">broadly flat</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              A-roads are the best-maintained class — but 9% in RED condition still means roughly 
              40km of principal routes needing maintenance. And A-roads are less than a tenth of the network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (2,327km — 45% of network)</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Survey period</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020–22</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">14%</td>
                    <td className="px-4 py-2 text-center">11%</td>
                    <td className="px-4 py-2 text-center">75%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2022–24</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">13%</td>
                    <td className="px-4 py-2 text-center font-bold">11%</td>
                    <td className="px-4 py-2 text-center font-bold">75%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              A marginal improvement — RED B/C roads down one percentage point — but 13% still means 
              roughly 302km of classified local roads in RED condition. A quarter of the B/C network 
              is amber or red and will need maintenance soon.
            </p>
          </div>

          {/* Unclassified */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-4">Unclassified roads (2,388km — 46% of network)</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">16%</div>
                <div className="text-sm text-rose-800">RED (2020–24)</div>
                <div className="text-xs text-surface-500">only published period</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">11%</div>
                <div className="text-sm text-amber-800">Amber</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">73%</div>
                <div className="text-sm text-emerald-800">Green</div>
              </div>
            </div>
            <p className="text-rose-800 text-sm">
              16% RED means approximately 382km of village lanes, estate roads and farm tracks in 
              the worst condition category — and that figure dates from a survey window ending in 2024, 
              on a network the council has not re-surveyed since 2020.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">AMBER Spend On A Massive Network</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-amber-400">£33.7m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">£35.7m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">88.8%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Shropshire spends slightly above its DfT allocation and projects nearly 89% preventative 
              maintenance — yet still earns an <span className="text-amber-400">AMBER Spend scorecard</span>. 
              On a 5,166km network, even well-directed funding cannot keep pace with winter deterioration 
              the council itself documents on roads it barely measures.
            </p>
          </div>
        </div>
      </section>

      {/* The Unclassified Blind Spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 2,388km Blind Spot
          </h2>
          <p className="text-surface-600 mb-8">
            46% of the network — surveyed every four years at most, and not since 2020
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The surveys are carried out every two years on main roads and every four years on minor roads; main roads are mostly A and B roads, minor roads include most C roads and unclassified roads. In 2024 surveys were delayed until this year. The unclassified network hasn't been surveyed since 2020, and is due to be surveyed by 2026. Evidence from reactive repairs suggests that this area of the network has deteriorated through recent winters, and we aim to measure the scale of this deterioration as soon as possible."
              source="Shropshire Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Survey frequency</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Last condition survey</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">A and B roads (main)</td>
                  <td className="px-6 py-4 text-center">Every 2 years</td>
                  <td className="px-6 py-4 text-center">2022–24 data published</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">C roads (minor)</td>
                  <td className="px-6 py-4 text-center">Every 4 years</td>
                  <td className="px-6 py-4 text-center">Included in 2022–24 B/C data</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Unclassified (minor)</td>
                  <td className="px-6 py-4 text-center font-bold">Every 4 years</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2020 — not due until 2026</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Four-Year Gap</h3>
              <p className="text-surface-600 text-sm mb-3">
                If your pothole was on an unclassified road, Shropshire's own report confirms there has been 
                <strong> no condition survey since 2020</strong> — a gap of up to six years by the time the 
                next survey is due in 2026. The 16% RED figure is from a 2020–24 window that may already 
                understate current deterioration.
              </p>
              <p className="text-surface-600 text-sm">
                For C roads, the four-year cycle means condition data published in 2022–24 may be two years 
                stale before the next survey — on roads the council groups with unclassified as "minor."
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Visual Surveys, Not SCANNER</h3>
              <p className="text-amber-800 text-sm mb-3">
                Shropshire uses structured visual surveys rather than the SCANNER laser system most councils 
                report to the DfT. The council states these surveys "provide a better representation of road 
                condition compared to SCANNER surveys, and are more closely related to the experience of 
                highway users in Shropshire."
              </p>
              <p className="text-amber-800 text-sm">
                Different methodology, different reporting periods — but the survey gaps and deterioration 
                admissions are documented in the council's own words regardless of technology.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for 
              knowing the condition of its roads. For Shropshire's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — 2020, or never within the claim period?</li>
              <li>• If the council admits deterioration through recent winters, what was done about your road?</li>
              <li>• How does a four-year survey cycle catch defects forming after ice and heavy rainfall?</li>
              <li>• Does reactive pothole filling count as "knowing" road condition, or just cleaning up after it?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed network knowledge on roads it has not condition-surveyed 
              in six years — while admitting they have got worse.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            159,272 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair on England's rural heartland
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Roads resurfaced (miles)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Preventative / reactive split</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">24,362</td>
                  <td className="px-6 py-4 text-center">113</td>
                  <td className="px-6 py-4 text-center">90.8% / 9.2%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">27,217</td>
                  <td className="px-6 py-4 text-center">84</td>
                  <td className="px-6 py-4 text-center">91.5% / 8.5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">33,785</td>
                  <td className="px-6 py-4 text-center">146</td>
                  <td className="px-6 py-4 text-center">91.2% / 8.8%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">39,615</td>
                  <td className="px-6 py-4 text-center">88</td>
                  <td className="px-6 py-4 text-center">86.4% / 13.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">34,293</td>
                  <td className="px-6 py-4 text-center">66</td>
                  <td className="px-6 py-4 text-center">75.2% / 24.8%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">159,272</td>
                  <td className="px-6 py-4 text-center font-bold">497</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~87 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Shropshire fills around 87 potholes per day across its 
                5,166km network. Pothole fillings peaked at 39,615 in 2023/24 — the same period 
                the council links to severe winter damage. A network producing defects at that rate 
                is one where potholes routinely form between inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Reactive Spike in 2024/25</h3>
              <p className="text-amber-800 text-sm">
                Reactive maintenance jumped to 24.8% of total spend in 2024/25 — up from 8.5–13.6% 
                in prior years — even as pothole fillings fell slightly to 34,293. The council projects 
                another 34,000 pothole repairs in 2025/26 and states 99% of defects repaired permanently 
                in 2024/25. Permanent patches still mean the defect existed in the first place.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Winter Deterioration Admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Winter Deterioration Admission
          </h2>
          <p className="text-surface-600 mb-8">
            The council's own explanation for damage on roads it has stopped measuring
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="We understand that the impacts of climate change are already being felt. Recent winters have brought severe conditions, including ice and significant rainfall, leading to increased damage to the network. As well as additional potholes, landslips have been a particular issue, prompting the use of the extra funding to address them."
              source="Shropshire Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Shropshire has a large highway network, covering over 5,100 km, and it's an intrinsic part of life in Shropshire. Due to limited alternative options, it's critically important for connecting communities, businesses and key services, and supports almost all travel in the county whether by car, public transport, cycle or on foot."
              source="Shropshire Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Admission Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Shropshire formally links recent winter severity to increased network damage — including 
                on unclassified roads it admits have deteriorated but not re-surveyed. That is documented 
                knowledge of elevated risk across a rural network with few transport alternatives.
              </p>
              <p className="text-surface-600 text-sm">
                Knowledge of accelerated winter wear raises the standard for what a "reasonable" inspection 
                frequency looks like — especially on minor roads surveyed only every four years.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Did the council increase safety inspections after admitting winter deterioration?</li>
                <li>• Was your road on the council's "resilient network" receiving earlier treatment?</li>
                <li>• If damage was known to be accelerating, why wasn't the defect caught before it damaged your vehicle?</li>
                <li>• Were 2024 surveys delayed on the road where your incident occurred?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What all-AMBER means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against An All-AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Shropshire is neither Derbyshire nor Buckinghamshire — here's how that shapes your claim
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 88.8% projected preventative spend — genuine asset management focus
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 99% permanent reactive repairs in 2024/25 — right-first-time approach
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> DfT Special Recognition Award 2024 for collaborative highway innovation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition stable at 9% RED across both survey periods
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> B/C RED condition marginally improved (14% → 13%)
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on well-surveyed A and B roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER on all four scorecards — condition, spend and best practice
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 2,388km unclassified roads not condition-surveyed since 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 16% of U-roads RED at last survey — ~382km in worst condition
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 159,272 potholes filled in five years — defects outpace inspections
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Admitted winter deterioration on roads the council stopped measuring
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Minor roads surveyed only every four years — C and U classes
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with documented asset management and permanent repair rates, your claim lives or dies on 
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on an unclassified road, the six-year survey gap is your strongest structural argument</li>
              <li>• Timing relative to admitted winter deterioration and the 2023/24 pothole peak of 39,615</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Shropshire's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Shropshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A rural network demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No four-year survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No winter deterioration admission cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 2,388km unsurveyed since 2020 documented</li>
                <li>✅ 159,272 potholes in five years cited</li>
                <li>✅ Winter deterioration admission argued</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Shropshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=shropshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Shropshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Shropshire is AMBER on all four DfT scorecards — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. An AMBER overall rating does not block claims under Section 41 of the Highways Act 1980. 
                Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected 
                and repaired — not on aggregate scorecard colours. Shropshire's own report admits unclassified roads 
                have deteriorated through recent winters while 46% of its network has not been condition-surveyed since 2020.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on an unclassified or C road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 2,388km — 46% of Shropshire's network — and minor roads (most C roads 
                and all unclassified roads) are surveyed only every four years. The council states the unclassified 
                network has not been surveyed since 2020 and is not due until 2026. At the last available survey 
                (2020–24), 16% of unclassified roads were in RED condition — roughly 382km of village routes, 
                estate roads and rural lanes.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Shropshire's high preventative spend weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily. Shropshire projects 88.8% preventative spend in 2025/26 and earned an AMBER Spend 
                scorecard — but preventative surface dressing does not eliminate potholes forming between inspections. 
                The council still expects to fill around 35,000 potholes in 2025/26. Your claim depends on whether 
                the specific defect was known about and repaired in time, not on the council's aggregate spend split.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the council's admission that unclassified roads have deteriorated help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Shropshire states that "evidence from reactive repairs suggests that this area of the network 
                has deteriorated through recent winters" on roads it has not condition-surveyed since 2020. That is 
                documented knowledge of elevated deterioration risk on 46% of the network — which raises the bar for 
                what a reasonable inspection regime looks like under Section 58.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Shropshire filled 34,293 potholes in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Filling potholes is reactive maintenance, not proof the underlying road is sound. Shropshire 
                filled 34,293 potholes in 2024/25 — about 94 every day — while B and C roads still had 13% in RED 
                condition at the last survey and the council projects another 34,000 pothole repairs in 2025/26. 
                A network producing defects at that rate is one where potholes routinely form between inspections.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why doesn't Shropshire publish year-by-year SCANNER data like other councils?
              </h3>
              <p className="text-surface-600 text-sm">
                Shropshire uses structured visual surveys rather than SCANNER laser surveys, stating these 
                "provide a better representation of road condition compared to SCANNER surveys." It publishes 
                condition data in multi-year periods (2020–22, 2022–24, 2020–24) rather than annual breakdowns. 
                For your claim, the survey gaps and the council's own deterioration admissions matter more than 
                the survey technology.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a
              href="https://next.shropshire.gov.uk/roads-and-highways/plans-policies-and-strategies/asset-management/local-highways-maintenance-transparency-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Shropshire Council Local Highways Maintenance Transparency Report (2025–2026)
            </a>.
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
